import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { servicesData } from '../mock';
import { ChefHat, ShoppingCart, Stethoscope, Heart, Clock, Award, ChevronDown, Star, Phone } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';

const iconMap = {
  ChefHat: <ChefHat size={32} />,
  ShoppingCart: <ShoppingCart size={32} />,
  Stethoscope: <Stethoscope size={32} />,
  Heart: <Heart size={32} />
};

export const PersonalCare = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const data = servicesData.personalCare;

  const processSteps = [
    {
      number: '01',
      title: 'Contact Initial',
      description: 'Appelez-nous pour discuter de vos besoins spécifiques'
    },
    {
      number: '02',
      title: 'Évaluation Gratuite',
      description: 'Visite à domicile pour évaluer vos besoins et établir un devis'
    },
    {
      number: '03',
      title: 'Sélection de l\'Intervenant',
      description: 'Nous choisissons le professionnel le plus adapté à votre situation'
    },
    {
      number: '04',
      title: 'Début de l\'Accompagnement',
      description: 'Début des prestations selon votre planning personnalisé'
    }
  ];

  const testimonials = [
    {
      name: 'Madame Dubois',
      age: '78 ans',
      rating: 5,
      comment: 'Grâce à l\'aide de Marie, je peux rester chez moi en toute sérénité. Elle est attentionnée et professionnelle.',
      service: 'Aide quotidienne'
    },
    {
      name: 'Famille Rousseau',
      rating: 5,
      comment: 'L\'accompagnement de notre père aux rendez-vous médicaux nous rassure énormément. Service impeccable.',
      service: 'Accompagnement médical'
    }
  ];

  const faqs = [
    {
      question: 'Quelles sont les qualifications de vos intervenants ?',
      answer: 'Tous nos intervenants sont diplômés et formés aux métiers de l\'aide à la personne. Ils suivent régulièrement des formations continues pour maintenir leurs compétences à jour.'
    },
    {
      question: 'Puis-je choisir mon intervenant ?',
      answer: 'Oui, nous vous proposons de rencontrer l\'intervenant avant le début de la prestation pour vous assurer d\'un bon feeling.'
    },
    {
      question: 'Quels sont les tarifs ?',
      answer: 'Nos tarifs varient selon le type de prestation et la fréquence. Contactez-nous pour un devis personnalisé gratuit. N\'oubliez pas le crédit d\'impôt de 50% !'
    },
    {
      question: 'Intervenez-vous le week-end ?',
      answer: 'Oui, nous sommes disponibles 7j/7, y compris les week-ends et jours fériés selon vos besoins.'
    }
  ];

  return (
    <div className="page-service">
      <section className="service-hero">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <h1 className="display-medium">{data.title}</h1>
            <p className="body-medium hero-subtitle">{data.description}</p>
          </ScrollReveal>
        </div>
      </section>

      <section className="service-image-section">
        <div className="container">
          <ScrollReveal animation="zoom-in">
            <img 
              src="/images/home-care.jpg" 
              alt="Aides à la personne" 
              className="service-main-image"
            />
          </ScrollReveal>
        </div>
      </section>

      {/* Services Details */}
      <section className="service-details">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Nos Prestations</h2>
              <p className="body-medium">Un accompagnement personnalisé pour votre quotidien</p>
            </div>
          </ScrollReveal>
          <div className="network-grid">
            {data.services.map((service, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="network-card service-detail-card">
                  <div className="feature-icon">{iconMap[service.icon]}</div>
                  <h3 className="heading-2">{service.title}</h3>
                  <p className="body-medium">{service.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="process-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Comment ça marche ?</h2>
              <p className="body-medium">Un processus simple en 4 étapes</p>
            </div>
          </ScrollReveal>
          <div className="process-steps">
            {processSteps.map((step, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 150}>
                <div className="process-step">
                  <div className="process-number">{step.number}</div>
                  <h3 className="heading-2">{step.title}</h3>
                  <p className="body-medium">{step.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="service-benefits">
        <div className="container">
          <div className="benefits-grid">
            <ScrollReveal animation="fade-right">
              <div className="benefits-content">
                <h2 className="heading-1">Pourquoi choisir nos services ?</h2>
                <ul className="benefits-list">
                  <li className="body-medium">
                    <Clock size={24} />
                    <span>Horaires flexibles adaptés à vos besoins</span>
                  </li>
                  <li className="body-medium">
                    <Award size={24} />
                    <span>Personnel qualifié et expérimenté</span>
                  </li>
                  <li className="body-medium">
                    <Heart size={24} />
                    <span>Approche bienveillante et respectueuse</span>
                  </li>
                  <li className="body-medium">
                    <Star size={24} />
                    <span>50% de crédit d'impôt sur les prestations</span>
                  </li>
                </ul>
                <Link to="/contact#devis" className="btn-primary">Demander un devis</Link>
              </div>
            </ScrollReveal>
            <ScrollReveal animation="fade-left" delay={200}>
              <div className="benefits-image">
                <div className="stats-highlight">
                  <div className="stat-item">
                    <div className="stat-value">98%</div>
                    <div className="stat-label">Satisfaction</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">10 ans</div>
                    <div className="stat-label">d'Expérience</div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Témoignages</h2>
              <p className="body-medium">Ce que nos clients disent de nous</p>
            </div>
          </ScrollReveal>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="testimonial-card">
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} fill="var(--brand-primary)" color="var(--brand-primary)" />
                    ))}
                </div>
                <p className="testimonial-comment">"{testimonial.comment}"</p>
                <div className="testimonial-author">
                  <div className="author-info">
                    <strong>{testimonial.name}</strong>
                    <span>{testimonial.service}</span>
                  </div>
                </div>
              </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Questions Fréquentes</h2>
            </div>
          </ScrollReveal>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 50}>
                <div className="faq-item">
                  <button 
                    className={`faq-question ${openFaq === index ? 'active' : ''}`}
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown size={24} className={`faq-icon ${openFaq === index ? 'rotated' : ''}`} />
                  </button>
                  {openFaq === index && (
                    <div className="faq-answer">
                      <p className="body-medium">{faq.answer}</p>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="cta-content">
              <h2 className="heading-1">Besoin d'aide à domicile ?</h2>
              <p className="body-medium">
                Contactez-nous pour discuter de vos besoins et obtenir un devis gratuit
              </p>
              <div className="cta-buttons">
                <Link to="/contact#devis" className="btn-primary btn-cta">Demander un devis</Link>
                <a href="tel:0481119591" className="btn-secondary btn-cta">
                  <Phone size={20} />
                  04 81 11 95 91
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};